import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import emailjs from '@emailjs/browser';

// ── Data ──────────────────────────────────────────────────────────────────────

const coreCapabilities = [
  {
    icon: '🔍',
    title: '"Google With a Face"',
    subtitle: 'Encyclopedic Medical Intelligence',
    desc: 'Vedant is an encyclopedic expert. He can answer medical queries, explain clinic policies, provide instant information about doctors and timings — all with a human-like smile and conversational tone.',
    color: '#3B82F6',
  },
  {
    icon: '🎙️',
    title: 'Voice-Command Identity',
    subtitle: 'Personalised AI Presence',
    desc: 'Customise your assistant\'s name via voice. Simply tell him "From now on, your name is John," and his identity updates instantly. Each clinic owns a uniquely personalised AI presence.',
    color: '#8B5CF6',
  },
  {
    icon: '👁️',
    title: 'Voice & Face Recognition',
    subtitle: 'VIP Patient Experience',
    desc: 'Vedant greets returning patients by name. He recognises voices and faces, creating a personalised "VIP" experience for every visitor — building patient loyalty from the very first interaction.',
    color: '#06B6D4',
  },
];

const humanoidInfrastructure = [
  {
    badge: 'VISUALS',
    badgeColor: '#3B82F6',
    icon: '🖥️',
    title: 'Life-Size Presence',
    desc: 'A 5\'5" vertical HD screen featuring a stunningly realistic avatar that breathes, blinks, and reacts to the room. Patients interact with Vedant as naturally as they would a human receptionist.',
  },
  {
    badge: 'INTELLIGENCE',
    badgeColor: '#8B5CF6',
    icon: '🧠',
    title: 'OpenAI Core',
    desc: 'Powered by the most advanced neural networks. Vedant understands context and medical terminology in multiple languages — delivering accurate, empathetic, and contextually relevant responses.',
  },
  {
    badge: 'ACTION',
    badgeColor: '#22C55E',
    icon: '📅',
    title: 'HealHub Core Sync',
    desc: 'Every booking and check-in handled by Vedant is instantly updated across your Clinic, Doctor, and Pharmacy dashboards inside HealHub — zero manual entry, zero lag.',
  },
];

const futureVision = [
  {
    icon: '💓',
    title: 'Contactless Vitals',
    desc: 'Using computer vision to detect heart rate, respiratory rate, and fever before the patient reaches the doctor.',
  },
  {
    icon: '🌐',
    title: 'Multilingual Bridge',
    desc: 'Actively translating between 22+ Indian languages to break communication barriers between doctors and patients.',
  },
  {
    icon: '😊',
    title: 'Sentiment Triage',
    desc: 'Analysing voice tone and facial expressions to prioritise patients in acute pain or distress automatically.',
  },
  {
    icon: '💳',
    title: 'Touchless Financials',
    desc: 'Managing insurance pre-authorisations and "Face-Pay" transactions directly on the life-size screen.',
  },
  {
    icon: '💊',
    title: 'Medication Coach',
    desc: 'Visually demonstrating pill dosages and usage instructions to patients as they leave the clinic.',
  },
  {
    icon: '📱',
    title: 'Digital Twin App',
    desc: 'The same Vedant avatar follows up with patients via the HealHub app to ensure recovery and follow-ups.',
  },
];

const operationalSavings = [
  { value: '75%', label: 'Reduction in front-desk expenses yearly', icon: '💰' },
  { value: '24/7', label: 'Availability — never calls in sick', icon: '⏰' },
  { value: '0', label: 'Training time required after setup', icon: '🎓' },
  { value: '22+', label: 'Indian languages supported', icon: '🗣️' },
];

const healhubBenefits = [
  {
    icon: '📊',
    title: 'Instant Dashboard Sync',
    desc: 'Every patient interaction — check-ins, bookings, queries — syncs to your HealHub dashboard in real time.',
  },
  {
    icon: '📋',
    title: 'Auto Patient Registration',
    desc: 'Vedant collects patient details, creates records, and updates the queue — before the patient reaches the doctor.',
  },
  {
    icon: '💊',
    title: 'Pharmacy Coordination',
    desc: 'Post-consultation, Vedant guides patients to the pharmacy, confirms prescriptions, and updates inventory.',
  },
  {
    icon: '📈',
    title: 'Subscription Value Multiplier',
    desc: 'Vedant is exclusively available to HealHub subscribers. It is the most powerful reason to upgrade and stay subscribed.',
  },
];

// ── Pilot Form ────────────────────────────────────────────────────────────────

function PilotForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    clinicName: '',
    city: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.clinicName) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await emailjs.send(
        'service_g9tvszg',
        'template_124o1la',
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          company: `Vedant Pilot — ${formData.clinicName}, ${formData.city}`,
          message: `Vedant Pilot Program Application\n\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nClinic: ${formData.clinicName}\nCity: ${formData.city}\nMessage: ${formData.message || 'Not provided'}`,
          to_name: 'Ashish Uniyal',
        },
        'u6gb-CCFGNTwlW3Ot'
      );
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please email us at a.uniyal@redcubicalsystems.com');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(59,130,246,0.2)',
    borderRadius: '10px',
    padding: '14px 16px',
    fontSize: '14px',
    color: '#fff',
    fontFamily: 'Inter, sans-serif',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: '700',
    color: 'rgba(255,255,255,0.4)',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: '6px',
  };

  if (submitted) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '48px 32px',
        background: 'rgba(139,92,246,0.06)',
        border: '1px solid rgba(139,92,246,0.2)',
        borderRadius: '16px',
      }}>
        <p style={{ fontSize: '48px', marginBottom: '16px' }}>🤖</p>
        <h3 style={{
          fontFamily: 'Barlow Condensed, sans-serif',
          fontSize: '26px', fontWeight: '900',
          color: '#fff', textTransform: 'uppercase',
          marginBottom: '10px',
        }}>
          Application Received!
        </h3>
        <p style={{
          fontSize: '14px',
          color: 'rgba(255,255,255,0.5)',
          lineHeight: '1.7',
        }}>
          Ashish will personally review your pilot application
          and reach out within 48 hours.
          Your clinic could be among India's first to run Vedant.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
    >
      {/* Row 1 — Name + Clinic */}
      <div className="vedant-form-row">
        <div>
          <label style={labelStyle}>Your Name *</label>
          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Dr. / Owner name"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#8B5CF6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
        <div>
          <label style={labelStyle}>Clinic Name *</label>
          <input
            name="clinicName"
            value={formData.clinicName}
            onChange={handleChange}
            placeholder="Your clinic name"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#8B5CF6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
      </div>

      {/* Row 2 — Email + Phone */}
      <div className="vedant-form-row">
        <div>
          <label style={labelStyle}>Email *</label>
          <input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@clinic.com"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#8B5CF6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
        <div>
          <label style={labelStyle}>Phone *</label>
          <input
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+91 98765 43210"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#8B5CF6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
      </div>

      {/* City */}
      <div>
        <label style={labelStyle}>City</label>
        <input
          name="city"
          value={formData.city}
          onChange={handleChange}
          placeholder="Your city"
          style={inputStyle}
          onFocus={e => (e.target.style.borderColor = '#8B5CF6')}
          onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
        />
      </div>

      {/* Message */}
      <div>
        <label style={labelStyle}>Tell us about your clinic (optional)</label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="How many patients per day? What challenges does your front desk face?"
          rows={3}
          style={{
            ...inputStyle,
            resize: 'vertical',
            minHeight: '90px',
          }}
          onFocus={e => (e.target.style.borderColor = '#8B5CF6')}
          onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
        />
      </div>

      {error && (
        <p style={{
          fontSize: '13px', color: '#E63946',
          background: 'rgba(230,57,70,0.08)',
          border: '1px solid rgba(230,57,70,0.2)',
          padding: '10px 14px', borderRadius: '8px',
        }}>
          ⚠️ {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="btn-blue"
        style={{
          width: '100%',
          justifyContent: 'center',
          padding: '16px',
          fontSize: '14px',
          opacity: submitting ? 0.7 : 1,
          cursor: submitting ? 'not-allowed' : 'pointer',
        }}
      >
        {submitting
          ? '⏳ Submitting...'
          : '🤖  Apply for Vedant Pilot Program'}
      </button>

      <p style={{
        fontSize: '11px',
        color: 'rgba(255,255,255,0.25)',
        textAlign: 'center',
        lineHeight: '1.6',
      }}>
        Requires active HealHub subscription ·
        Limited pilot slots available · Dehradun-region priority
      </p>
    </form>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Vedant() {
  const [activeCapability, setActiveCapability] = useState(0);

  return (
    <div className="page-wrapper" style={{ paddingTop: '80px' }}>
      <SEOHead
        title="Vedant — World's First AI Receptionist for Clinics"
        description="Vedant is the world's first life-size AI receptionist for modern healthcare. A 5'5 human-like AI presence that automates your clinic's front desk, syncs with HealHub, and reduces operational costs by 75%. Exclusively for HealHub subscribers."
        canonical="/vedant"
        keywords="AI receptionist clinic India, Vedant AI, HealHub Vedant, AI front desk healthcare, clinic automation India, AI healthcare assistant India"
      />

      {/* ── HERO ── */}
      <section
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          padding: '80px 32px',
          position: 'relative',
          overflow: 'hidden',
          background: `
            radial-gradient(ellipse 80% 60% at 50% -10%,
              rgba(139,92,246,0.35) 0%,
              rgba(59,130,246,0.15) 40%,
              transparent 70%),
            var(--black)
          `,
        }}
      >
        {/* Decorative orbs */}
        <div style={{
          position: 'absolute', top: '-200px', right: '-200px',
          width: '700px', height: '700px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-150px', left: '-150px',
          width: '600px', height: '600px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div style={{
          maxWidth: '1280px', margin: '0 auto',
          width: '100%', position: 'relative',
        }}>
          <div className="vedant-hero-grid">

            {/* Left — Content */}
            <div>
              {/* Badges */}
              <div style={{
                display: 'flex', gap: '12px',
                flexWrap: 'wrap', marginBottom: '32px',
              }}>
                <span className="badge" style={{
                  background: 'rgba(139,92,246,0.1)',
                  borderColor: 'rgba(139,92,246,0.3)',
                  color: '#A855F7',
                }}>
                  🤖 &nbsp; AI · Healthcare
                </span>
                <span style={{
                  fontSize: '11px', fontWeight: '700',
                  color: '#A855F7',
                  background: 'rgba(168,85,247,0.1)',
                  border: '1px solid rgba(168,85,247,0.3)',
                  padding: '6px 14px', borderRadius: '50px',
                  letterSpacing: '1px', textTransform: 'uppercase',
                }}>
                  ● In Development
                </span>
                <span style={{
                  fontSize: '11px', fontWeight: '700',
                  color: '#F59E0B',
                  background: 'rgba(245,158,11,0.1)',
                  border: '1px solid rgba(245,158,11,0.3)',
                  padding: '6px 14px', borderRadius: '50px',
                  letterSpacing: '1px', textTransform: 'uppercase',
                }}>
                  🌍 India's First
                </span>
              </div>

              <h1 className="display-heading" style={{ marginBottom: '8px' }}>
                <span style={{
                  background: 'linear-gradient(135deg, #A855F7 0%, #8B5CF6 50%, #6366F1 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>VEDANT</span>
              </h1>

              <p style={{
                fontSize: 'clamp(16px, 2vw, 20px)',
                color: 'rgba(255,255,255,0.5)',
                letterSpacing: '3px',
                textTransform: 'uppercase',
                fontWeight: '600',
                marginBottom: '32px',
              }}>
                The World's First Life-Size AI Receptionist
              </p>

              <p style={{
                fontSize: 'clamp(16px, 2vw, 19px)',
                color: 'rgba(255,255,255,0.65)',
                lineHeight: '1.8',
                maxWidth: '560px',
                marginBottom: '16px',
              }}>
                A <strong style={{ color: '#A855F7' }}>5'5" human-like AI presence</strong> that
                doesn't just manage your front desk — it transforms the patient experience.
                Powered by OpenAI. Synced with HealHub. Built for the future of healthcare.
              </p>

              {/* HealHub exclusive callout */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center', gap: '10px',
                background: 'rgba(34,197,94,0.08)',
                border: '1px solid rgba(34,197,94,0.25)',
                borderRadius: '12px',
                padding: '12px 20px',
                marginBottom: '40px',
              }}>
                <span style={{ fontSize: '18px' }}>🏥</span>
                <p style={{
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.7)',
                  lineHeight: '1.5',
                }}>
                  <strong style={{ color: '#22C55E' }}>Exclusively for HealHub subscribers.</strong>
                  {' '}Subscribe to HealHub to unlock Vedant for your clinic.
                </p>
              </div>

              <div style={{
                display: 'flex', gap: '12px',
                flexWrap: 'wrap', marginBottom: '48px',
              }}>
                <a
                  href="https://healhub.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  Subscribe to HealHub ↗
                </a>
                <button
                  onClick={() => {
                    document.getElementById('pilot')
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn-outline"
                >
                  Apply for Pilot →
                </button>
              </div>

              {/* Stat strip */}
              <div style={{
                display: 'flex', gap: '32px', flexWrap: 'wrap',
              }}>
                {[
                  { icon: '💰', label: '75% Cost Reduction' },
                  { icon: '🌐', label: '22+ Languages' },
                  { icon: '⏰', label: '24/7 Availability' },
                ].map((item) => (
                  <div key={item.label} style={{
                    display: 'flex', alignItems: 'center',
                    gap: '8px', fontSize: '13px',
                    color: 'rgba(255,255,255,0.45)',
                  }}>
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Avatar Visual */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <div style={{
                position: 'relative',
                width: '260px',
              }}>
                {/* Glow behind avatar */}
                <div style={{
                  position: 'absolute',
                  top: '50%', left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '300px', height: '500px',
                  background: 'radial-gradient(ellipse, rgba(139,92,246,0.25) 0%, transparent 70%)',
                  pointerEvents: 'none',
                }} />

                {/* Screen frame */}
                <div style={{
                  width: '260px',
                  background: 'rgba(10,14,26,0.95)',
                  border: '2px solid rgba(139,92,246,0.3)',
                  borderRadius: '24px',
                  padding: '24px 20px',
                  boxShadow: '0 40px 100px rgba(139,92,246,0.2), 0 0 0 1px rgba(255,255,255,0.05)',
                  position: 'relative',
                }}>
                  {/* Screen label */}
                  <div style={{
                    textAlign: 'center', marginBottom: '20px',
                  }}>
                    <p style={{
                      fontSize: '10px', fontWeight: '700',
                      color: 'rgba(139,92,246,0.7)',
                      letterSpacing: '3px', textTransform: 'uppercase',
                    }}>
                      VEDANT AI · ONLINE
                    </p>
                    <div style={{
                      width: '8px', height: '8px',
                      background: '#22C55E', borderRadius: '50%',
                      margin: '6px auto 0',
                      boxShadow: '0 0 8px rgba(34,197,94,0.8)',
                    }} />
                  </div>

                  {/* Avatar face */}
                  <div style={{
                    width: '120px', height: '120px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(139,92,246,0.3) 0%, rgba(59,130,246,0.2) 100%)',
                    border: '2px solid rgba(139,92,246,0.4)',
                    margin: '0 auto 20px',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '56px',
                    position: 'relative',
                  }}>
                    🤖
                    {/* Breathing ring */}
                    <div style={{
                      position: 'absolute', inset: '-6px',
                      borderRadius: '50%',
                      border: '1px solid rgba(139,92,246,0.2)',
                      animation: 'breathe 3s ease-in-out infinite',
                    }} />
                  </div>

                  {/* Greeting */}
                  <div style={{
                    background: 'rgba(139,92,246,0.08)',
                    border: '1px solid rgba(139,92,246,0.2)',
                    borderRadius: '12px',
                    padding: '14px',
                    marginBottom: '12px',
                    textAlign: 'center',
                  }}>
                    <p style={{
                      fontSize: '13px', fontWeight: '600',
                      color: '#fff', marginBottom: '4px',
                    }}>
                      "Namaste! Welcome to
                    </p>
                    <p style={{
                      fontSize: '13px', fontWeight: '600',
                      color: '#A855F7',
                    }}>
                      Sunrise Clinic."
                    </p>
                  </div>

                  {/* Action buttons */}
                  {[
                    { label: '📅  Book Appointment', color: '#3B82F6' },
                    { label: '👨‍⚕️  Find a Doctor', color: '#8B5CF6' },
                    { label: '❓  Ask a Question', color: '#06B6D4' },
                  ].map((btn) => (
                    <div key={btn.label} style={{
                      padding: '10px 14px',
                      background: 'rgba(255,255,255,0.03)',
                      border: `1px solid ${btn.color}25`,
                      borderRadius: '8px',
                      marginBottom: '8px',
                      display: 'flex', alignItems: 'center',
                      gap: '8px',
                    }}>
                      <div style={{
                        width: '6px', height: '6px',
                        borderRadius: '50%',
                        background: btn.color,
                        flexShrink: 0,
                      }} />
                      <span style={{
                        fontSize: '12px',
                        color: 'rgba(255,255,255,0.6)',
                      }}>
                        {btn.label}
                      </span>
                    </div>
                  ))}

                  {/* HealHub sync indicator */}
                  <div style={{
                    marginTop: '16px',
                    padding: '10px 14px',
                    background: 'rgba(34,197,94,0.06)',
                    border: '1px solid rgba(34,197,94,0.2)',
                    borderRadius: '8px',
                    display: 'flex', alignItems: 'center', gap: '8px',
                  }}>
                    <span style={{ fontSize: '14px' }}>🔄</span>
                    <span style={{
                      fontSize: '11px',
                      color: 'rgba(34,197,94,0.8)',
                      fontWeight: '600',
                    }}>
                      HealHub Synced · Live
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── OPERATIONAL SAVINGS STATS ── */}
      <section style={{
        background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(139,92,246,0.15)',
        borderBottom: '1px solid rgba(139,92,246,0.15)',
        padding: '0 32px',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}
          className="vedant-stats-grid">
          {operationalSavings.map((stat, i) => (
            <div key={i} style={{
              padding: '40px 24px', textAlign: 'center',
              borderRight: i < 3
                ? '1px solid rgba(139,92,246,0.12)' : 'none',
            }}>
              <p style={{ fontSize: '28px', marginBottom: '8px' }}>{stat.icon}</p>
              <p style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontSize: 'clamp(32px, 5vw, 56px)',
                fontWeight: '900', color: '#fff',
                lineHeight: 1, marginBottom: '8px',
              }}>
                {stat.value}
              </p>
              <p style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.4)',
                lineHeight: '1.4',
              }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── REVOLUTIONARY PRESENCE ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">A Revolutionary Presence</span>
            <h2 className="section-title" style={{ marginBottom: '20px' }}>
              VEDANT COMPLETELY<br />
              <span style={{
                background: 'linear-gradient(135deg, #A855F7 0%, #8B5CF6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                AUTOMATES YOUR FRONT DESK
              </span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              Eliminate the overhead of hiring and training human receptionists.
              Vedant provides a 24/7, error-free presence at a fraction of standard costs.
              Live in the future — today.
            </p>
          </div>

          {/* Interactive capability selector */}
          <div className="vedant-capabilities-grid">
            {/* Cards — left */}
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '16px',
            }}>
              {coreCapabilities.map((cap, i) => (
                <button
                  key={i}
                  onClick={() => setActiveCapability(i)}
                  style={{
                    padding: '28px 32px',
                    background: activeCapability === i
                      ? `rgba(${cap.color === '#3B82F6' ? '59,130,246'
                        : cap.color === '#8B5CF6' ? '139,92,246'
                        : '6,182,212'},0.12)`
                      : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${activeCapability === i
                      ? cap.color + '50'
                      : 'rgba(255,255,255,0.06)'}`,
                    borderRadius: '16px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.25s',
                    fontFamily: 'Inter, sans-serif',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                  }}
                >
                  <span style={{
                    fontSize: '28px', flexShrink: 0,
                  }}>
                    {cap.icon}
                  </span>
                  <div style={{ textAlign: 'left' }}>
                    <p style={{
                      fontSize: '15px', fontWeight: '700',
                      color: activeCapability === i
                        ? '#fff' : 'rgba(255,255,255,0.55)',
                      marginBottom: '4px',
                      transition: 'color 0.25s',
                    }}>
                      {cap.title}
                    </p>
                    <p style={{
                      fontSize: '12px',
                      color: activeCapability === i
                        ? cap.color : 'rgba(255,255,255,0.25)',
                      transition: 'color 0.25s',
                    }}>
                      {cap.subtitle}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            {/* Detail panel — right */}
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${coreCapabilities[activeCapability].color}30`,
              borderRadius: '24px',
              padding: '56px 48px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              minHeight: '360px',
            }}>
              <div style={{
                position: 'absolute', top: '-80px', right: '-80px',
                width: '350px', height: '350px', borderRadius: '50%',
                background: `radial-gradient(circle, ${coreCapabilities[activeCapability].color}15 0%, transparent 70%)`,
                pointerEvents: 'none',
                transition: 'background 0.4s',
              }} />

              <div style={{ position: 'relative' }}>
                <div style={{
                  width: '72px', height: '72px',
                  background: `${coreCapabilities[activeCapability].color}18`,
                  border: `1px solid ${coreCapabilities[activeCapability].color}40`,
                  borderRadius: '20px',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '36px', marginBottom: '28px',
                }}>
                  {coreCapabilities[activeCapability].icon}
                </div>

                <p style={{
                  fontSize: '11px', fontWeight: '700',
                  color: coreCapabilities[activeCapability].color,
                  letterSpacing: '3px', textTransform: 'uppercase',
                  marginBottom: '12px',
                }}>
                  {coreCapabilities[activeCapability].subtitle}
                </p>

                <h3 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: 'clamp(24px, 3vw, 38px)',
                  fontWeight: '900', color: '#fff',
                  textTransform: 'uppercase',
                  lineHeight: 1.05, marginBottom: '24px',
                }}>
                  {coreCapabilities[activeCapability].title}
                </h3>

                <p style={{
                  fontSize: '16px',
                  color: 'rgba(255,255,255,0.6)',
                  lineHeight: '1.8',
                  maxWidth: '480px',
                }}>
                  {coreCapabilities[activeCapability].desc}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HUMANOID INFRASTRUCTURE ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark-2)',
        borderTop: '1px solid rgba(139,92,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">The Technology Stack</span>
            <h2 className="section-title" style={{ marginBottom: '20px' }}>
              THE HUMANOID<br />
              <span className="gradient-text">INFRASTRUCTURE</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              Merging high-fidelity visual arts with Large Language Models
              to replace the traditional front desk — permanently.
            </p>
          </div>

          <div style={{
            display: 'grid', gap: '24px',
          }} className="humanoid-grid">
            {humanoidInfrastructure.map((item, i) => (
              <div
                key={i}
                style={{
                  padding: '48px 40px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(59,130,246,0.12)',
                  borderRadius: '20px',
                  transition: 'all 0.3s',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(59,130,246,0.06)';
                  e.currentTarget.style.borderColor = 'rgba(59,130,246,0.25)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                  e.currentTarget.style.borderColor = 'rgba(59,130,246,0.12)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Badge */}
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: `${item.badgeColor}15`,
                  border: `1px solid ${item.badgeColor}35`,
                  borderRadius: '50px',
                  padding: '4px 14px',
                  marginBottom: '24px',
                }}>
                  <div style={{
                    width: '6px', height: '6px',
                    borderRadius: '50%',
                    background: item.badgeColor,
                  }} />
                  <span style={{
                    fontSize: '10px', fontWeight: '800',
                    color: item.badgeColor,
                    letterSpacing: '2px',
                    textTransform: 'uppercase',
                  }}>
                    {item.badge}
                  </span>
                </div>

                <div style={{
                  width: '56px', height: '56px',
                  background: 'rgba(139,92,246,0.1)',
                  border: '1px solid rgba(139,92,246,0.2)',
                  borderRadius: '14px',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px', marginBottom: '20px',
                }}>
                  {item.icon}
                </div>

                <h3 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '24px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase',
                  marginBottom: '14px',
                }}>
                  {item.title}
                </h3>

                <p style={{
                  fontSize: '15px',
                  color: 'rgba(255,255,255,0.5)',
                  lineHeight: '1.7',
                }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Savings Banner */}
          <div style={{
            marginTop: '40px',
            background: 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(59,130,246,0.08) 100%)',
            border: '1px solid rgba(139,92,246,0.25)',
            borderRadius: '20px',
            padding: '40px 48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '24px',
          }}>
            <div>
              <h3 style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontSize: 'clamp(24px, 3vw, 36px)',
                fontWeight: '900', color: '#fff',
                textTransform: 'uppercase', marginBottom: '8px',
              }}>
                MASSIVE OPERATIONAL SAVINGS
              </h3>
              <p style={{
                fontSize: '15px',
                color: 'rgba(255,255,255,0.5)',
                lineHeight: '1.6', maxWidth: '560px',
              }}>
                Eliminate the overhead of hiring and training human receptionists.
                Vedant provides a 24/7, error-free presence at a fraction of standard costs.
              </p>
            </div>
            <div style={{
              background: 'rgba(139,92,246,0.1)',
              border: '1px solid rgba(139,92,246,0.3)',
              borderRadius: '16px',
              padding: '24px 32px',
              textAlign: 'center',
              flexShrink: 0,
            }}>
              <p style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontSize: '48px', fontWeight: '900',
                color: '#A855F7', lineHeight: 1, marginBottom: '4px',
              }}>
                75%
              </p>
              <p style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.5)',
              }}>
                Front-Desk Cost Reduction
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── HEALHUB CONNECTION ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="healhub-connection-grid">
            {/* Left */}
            <div>
              <span className="section-label">Exclusively for HealHub Clinics</span>
              <h2 className="section-title" style={{ marginBottom: '24px' }}>
                VEDANT IS YOUR<br />
                REASON TO JOIN<br />
                <span className="gradient-text">HEALHUB</span>
              </h2>
              <p className="body-text" style={{ marginBottom: '24px', maxWidth: '480px' }}>
                Vedant is not a standalone product. It is a powerful extension of your 
                HealHub subscription — available exclusively to clinics already on the 
                HealHub platform. Every Vedant interaction feeds directly into your 
                HealHub dashboard in real time.
              </p>
              <p className="body-text" style={{ marginBottom: '40px', maxWidth: '480px' }}>
                Want Vedant for your clinic? The first step is a HealHub subscription. 
                Once you are on HealHub, you are first in line to receive Vedant when 
                it launches in your city.
              </p>

              <div style={{
                display: 'flex', gap: '12px', flexWrap: 'wrap',
              }}>
                <a
                  href="https://healhub.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  Subscribe to HealHub ↗
                </a>
                <button
                  onClick={() => {
                    document.getElementById('pilot')
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn-outline"
                >
                  Apply for Pilot →
                </button>
              </div>
            </div>

            {/* Right — Benefits */}
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '16px',
            }}>
              {healhubBenefits.map((benefit, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex', gap: '20px', alignItems: 'flex-start',
                    padding: '28px',
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(59,130,246,0.12)',
                    borderRadius: '16px',
                    transition: 'all 0.3s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(59,130,246,0.06)';
                    e.currentTarget.style.borderColor = 'rgba(59,130,246,0.25)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                    e.currentTarget.style.borderColor = 'rgba(59,130,246,0.12)';
                  }}
                >
                  <div style={{
                    width: '48px', height: '48px',
                    background: 'rgba(139,92,246,0.1)',
                    border: '1px solid rgba(139,92,246,0.2)',
                    borderRadius: '12px',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '22px', flexShrink: 0,
                  }}>
                    {benefit.icon}
                  </div>
                  <div>
                    <h4 style={{
                      fontFamily: 'Barlow Condensed, sans-serif',
                      fontSize: '18px', fontWeight: '900',
                      color: '#fff', textTransform: 'uppercase',
                      marginBottom: '6px',
                    }}>
                      {benefit.title}
                    </h4>
                    <p style={{
                      fontSize: '14px',
                      color: 'rgba(255,255,255,0.5)',
                      lineHeight: '1.65',
                    }}>
                      {benefit.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FUTURE VISION ── */}
      <section style={{
        padding: '120px 32px',
        background: `
          radial-gradient(ellipse 70% 50% at 50% 0%,
            rgba(139,92,246,0.1) 0%, transparent 70%),
          var(--dark-2)
        `,
        borderTop: '1px solid rgba(139,92,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">Future Vision</span>
            <h2 className="section-title" style={{ marginBottom: '20px' }}>
              VEDANT IS EVOLVING TO BECOME<br />
              <span style={{
                background: 'linear-gradient(135deg, #A855F7 0%, #8B5CF6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                THE CENTRAL NERVOUS SYSTEM
              </span>
              <br />OF YOUR DIGITAL CLINIC
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              What you see today is just Phase 1. The roadmap goes far beyond reception.
            </p>
          </div>

          <div style={{
            display: 'grid', gap: '2px',
            background: 'rgba(139,92,246,0.08)',
            border: '1px solid rgba(139,92,246,0.12)',
            borderRadius: '20px', overflow: 'hidden',
          }} className="vision-grid">
            {futureVision.map((item, i) => (
              <div
                key={i}
                style={{
                  padding: '40px 36px',
                  background: 'rgba(5,8,16,0.85)',
                  transition: 'background 0.3s',
                  borderRight: i % 3 !== 2
                    ? '1px solid rgba(139,92,246,0.08)' : 'none',
                  borderBottom: i < 3
                    ? '1px solid rgba(139,92,246,0.08)' : 'none',
                }}
                onMouseEnter={e =>
                  (e.currentTarget.style.background = 'rgba(139,92,246,0.07)')
                }
                onMouseLeave={e =>
                  (e.currentTarget.style.background = 'rgba(5,8,16,0.85)')
                }
              >
                <div style={{
                  width: '52px', height: '52px',
                  background: 'rgba(139,92,246,0.1)',
                  border: '1px solid rgba(139,92,246,0.2)',
                  borderRadius: '12px',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px', marginBottom: '20px',
                }}>
                  {item.icon}
                </div>
                <h3 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '20px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase',
                  marginBottom: '12px',
                }}>
                  {item.title}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.5)',
                  lineHeight: '1.65',
                }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PILOT PROGRAM ── */}
      <section
        id="pilot"
        style={{
          padding: '120px 32px',
          background: `
            radial-gradient(ellipse 80% 60% at 50% 0%,
              rgba(139,92,246,0.12) 0%, transparent 70%),
            var(--black)
          `,
          borderTop: '1px solid rgba(139,92,246,0.15)',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="pilot-grid">

            {/* Left — Copy */}
            <div>
              <span className="section-label" style={{ color: '#A855F7' }}>
                Pilot Program
              </span>
              <h2 className="section-title" style={{ marginBottom: '24px' }}>
                BRING VEDANT<br />TO YOUR<br />
                <span style={{
                  background: 'linear-gradient(135deg, #A855F7, #8B5CF6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  CLINIC FIRST
                </span>
              </h2>
              <p className="body-text" style={{ marginBottom: '32px', maxWidth: '440px' }}>
                We are selecting a limited number of forward-thinking clinics to be 
                part of the Vedant pilot program. Pilot clinics get Vedant at 
                significantly reduced cost and direct access to our development team.
              </p>

              {/* Requirements */}
              <div style={{ marginBottom: '40px' }}>
                <p style={{
                  fontSize: '11px', fontWeight: '700',
                  color: 'rgba(255,255,255,0.3)',
                  letterSpacing: '2px', textTransform: 'uppercase',
                  marginBottom: '16px',
                }}>
                  Pilot Requirements
                </p>
                {[
                  { icon: '✅', text: 'Active HealHub subscription' },
                  { icon: '📍', text: 'Clinic in Uttarakhand or NCR (priority)' },
                  { icon: '👥', text: 'Minimum 20 patients per day' },
                  { icon: '🤝', text: 'Willingness to provide feedback' },
                ].map((req) => (
                  <div key={req.text} style={{
                    display: 'flex', alignItems: 'center',
                    gap: '12px', marginBottom: '12px',
                    fontSize: '15px',
                    color: 'rgba(255,255,255,0.6)',
                  }}>
                    <span style={{ fontSize: '16px', flexShrink: 0 }}>
                      {req.icon}
                    </span>
                    {req.text}
                  </div>
                ))}
              </div>

              {/* Pilot benefits */}
              <div style={{
                padding: '24px',
                background: 'rgba(139,92,246,0.06)',
                border: '1px solid rgba(139,92,246,0.2)',
                borderRadius: '16px',
              }}>
                <p style={{
                  fontSize: '13px', fontWeight: '700',
                  color: '#A855F7', marginBottom: '12px',
                  letterSpacing: '1px', textTransform: 'uppercase',
                }}>
                  What Pilot Clinics Get
                </p>
                {[
                  'Vedant at 60% pilot pricing',
                  'Priority deployment in your city',
                  'Direct line to our founding team',
                  'Co-development of features you need',
                ].map((benefit) => (
                  <div key={benefit} style={{
                    display: 'flex', alignItems: 'center',
                    gap: '8px', marginBottom: '8px',
                    fontSize: '14px', color: 'rgba(255,255,255,0.6)',
                  }}>
                    <span style={{ color: '#A855F7', flexShrink: 0 }}>→</span>
                    {benefit}
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Form */}
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(139,92,246,0.2)',
              borderRadius: '24px',
              padding: '48px 40px',
            }}>
              <p style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontSize: '22px', fontWeight: '900',
                color: '#fff', textTransform: 'uppercase',
                marginBottom: '8px',
              }}>
                Apply for the Pilot
              </p>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.4)',
                marginBottom: '28px',
              }}>
                Limited slots. HealHub subscription required.
              </p>
              <PilotForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{
        padding: '80px 32px',
        background: 'var(--dark-2)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{
            fontSize: '13px', fontWeight: '700',
            color: 'rgba(255,255,255,0.3)',
            letterSpacing: '3px', textTransform: 'uppercase',
            marginBottom: '16px',
          }}>
            Not yet on HealHub?
          </p>
          <h3 style={{
            fontFamily: 'Barlow Condensed, sans-serif',
            fontSize: 'clamp(28px, 4vw, 48px)',
            fontWeight: '900', color: '#fff',
            textTransform: 'uppercase', marginBottom: '16px',
            lineHeight: 1.05,
          }}>
            START WITH HEALHUB.<br />
            <span className="gradient-text">UNLOCK VEDANT.</span>
          </h3>
          <p className="section-subtitle" style={{
            margin: '0 auto 32px', fontSize: '16px',
          }}>
            Vedant is exclusively available to HealHub subscribers.
            Join 500+ clinics already on HealHub and be first in line
            when Vedant launches in your city.
          </p>
          <div style={{
            display: 'flex', gap: '16px',
            justifyContent: 'center', flexWrap: 'wrap',
          }}>
            <a
              href="https://healhub.in"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Get Started with HealHub ↗
            </a>
            <Link to="/contact" className="btn-outline">
              Talk to Our Team →
            </Link>
          </div>
        </div>
      </section>

      {/* ── RESPONSIVE + ANIMATIONS ── */}
      <style>{`
        @keyframes breathe {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.08); opacity: 0.6; }
        }
        .vedant-hero-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }
        .vedant-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
        }
        .vedant-capabilities-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          align-items: start;
        }
        .humanoid-grid {
          grid-template-columns: repeat(3, 1fr);
        }
        .healhub-connection-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: start;
        }
        .vision-grid {
          grid-template-columns: repeat(3, 1fr);
        }
        .pilot-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: start;
        }
        .vedant-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        @media (max-width: 1024px) {
          .humanoid-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .vision-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .vedant-capabilities-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 768px) {
          .vedant-hero-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .vedant-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .humanoid-grid {
            grid-template-columns: 1fr !important;
          }
          .healhub-connection-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .vision-grid {
            grid-template-columns: 1fr !important;
          }
          .pilot-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .vedant-form-row {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}